import type { ChangeEvent, ClipboardEvent, DragEvent, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ZendeskAttachmentPayload } from '../zendesk';

export interface Attachment {
  name: string;
  /** Object URL for the preview. */
  url: string;
  file: File;
}

export interface AttachmentsController {
  items: Attachment[];
  /** Ref for the hidden file input the "Add Image" button clicks. */
  inputRef: RefObject<HTMLInputElement>;
  remove: (url: string) => void;
  onPickFiles: (event: ChangeEvent<HTMLInputElement>) => void;
  /*
   * Bound on the create card, not on the attachments box: dropping or pasting
   * works anywhere in the form, which is what the hint under it promises.
   */
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onPaste: (event: ClipboardEvent<HTMLDivElement>) => void;
  /** Reads every image as the base64 `data:` URL the create action expects. */
  toPayload: () => Promise<ZendeskAttachmentPayload[]>;
}

const readDataUrl = async (file: File): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('load', () => {
      resolve(String(reader.result));
    });

    reader.addEventListener('error', () => {
      reject(new Error(`Could not read ${file.name}`));
    });

    reader.readAsDataURL(file);
  });

/*
 * Image attachments for a new ticket: the picked files, their preview URLs, and
 * every way of adding one (button, drop, paste). Only images are accepted.
 */
export const useAttachments = (): AttachmentsController => {
  const [items, setItems] = useState<Attachment[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<Attachment[]>([]);

  // Mirror of the current items, so the cleanup below sees the latest list.
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  /*
   * Revoked when the form closes rather than when the tab does: the card owns
   * this hook, so a cancelled draft stops holding its previews in memory.
   */
  useEffect(
    () => (): void => {
      itemsRef.current.forEach((item) => {
        URL.revokeObjectURL(item.url);
      });
    },
    []
  );

  const addFiles = (files: File[]): void => {
    const images = files.filter((file) => file.type.startsWith('image/'));

    if (images.length === 0) {
      return;
    }

    setItems((prev) => [
      ...prev,
      ...images.map((file) => ({ name: file.name, url: URL.createObjectURL(file), file }))
    ]);
  };

  return {
    items,
    inputRef,

    remove: (url: string): void => {
      URL.revokeObjectURL(url);
      setItems((prev) => prev.filter((item) => item.url !== url));
    },

    onPickFiles: (event: ChangeEvent<HTMLInputElement>): void => {
      if (event.target.files) {
        addFiles([...event.target.files]);
      }

      // Reset so re-picking the same file fires onChange again.
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },

    onDrop: (event: DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      addFiles([...event.dataTransfer.files]);
    },

    onDragOver: (event: DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
    },

    onPaste: (event: ClipboardEvent<HTMLDivElement>): void => {
      const files = [...event.clipboardData.items]
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);

      if (files.length > 0) {
        addFiles(files);
      }
    },

    toPayload: async (): Promise<ZendeskAttachmentPayload[]> =>
      Promise.all(
        items.map(async (item) => ({ name: item.name, dataUrl: await readDataUrl(item.file) }))
      )
  };
};
